import { History } from 'history';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import { OrganizationDetailFragment } from '../generated/graphql';
import ApiErrorHandler from '../service/ApiErrorHandler';
import ErrorPresenter from '../service/ErrorPresenter';
import IntercomService from '../service/IntercomService';
import { LogRocketService } from '../service/LogRocketService';
import AuthenticationWebApi from '../service/persistent/AuthenticationWebApi';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import UserRegistrationWebApi, { SignUpFailed } from '../service/persistent/UserRegistrationWebApi';
import UserWebApi, { MeNotFound } from '../service/persistent/UserWebApi';
import { disposeUser, finishAuthentication, setSignedInUser, startAuthentication } from '../thunk/authentication';
import { finishFetchingOrganizations, startFetchingOrganizations } from '../thunk/availableOrganizations';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import { SingleDirectorUserFragment } from './../generated/graphql';

class AuthenticationUsecase {
  private store: Store<State>;
  private history: History;
  private authenticationWebApi: AuthenticationWebApi;
  private apiErrorHandler: ApiErrorHandler;
  private errorPresenter: ErrorPresenter;
  private logRocketService: LogRocketService;
  private intercomService: IntercomService;
  private organizationWebApi: OrganizationWebApi;
  private userWebApi: UserWebApi;
  private userRegistrationWebApi: UserRegistrationWebApi;

  constructor({
    store,
    history,
    authenticationWebApi,
    apiErrorHandler,
    errorPresenter,
    logRocketService,
    intercomService,
    organizationWebApi,
    userWebApi,
    userRegistrationWebApi,
  }: {
    store: Store<State>;
    history: History;
    authenticationWebApi: AuthenticationWebApi;
    apiErrorHandler: ApiErrorHandler;
    errorPresenter: ErrorPresenter;
    logRocketService: LogRocketService;
    intercomService: IntercomService;
    organizationWebApi: OrganizationWebApi;
    userWebApi: UserWebApi;
    userRegistrationWebApi: UserRegistrationWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.authenticationWebApi = authenticationWebApi;
    this.apiErrorHandler = apiErrorHandler;
    this.errorPresenter = errorPresenter;
    this.logRocketService = logRocketService;
    this.intercomService = intercomService;
    this.organizationWebApi = organizationWebApi;
    this.userWebApi = userWebApi;
    this.userRegistrationWebApi = userRegistrationWebApi;
  }

  public needSignedIn = (): void => {
    const user = this.store.getState().authentication.signedInUser;

    if (!user) {
      const err = new Error('message.require_signing');
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public handleNeedSignedIn = (): void => {
    const err = new Error('message.require_signing');
    this.errorPresenter.showAsNonFatal(err);
    throw err;
  };

  public redirectKaizenIdforSignUp = (url: string): void => {
    const user = this.store.getState().authentication.signedInUser;
    user ? this.history.replace('/') : location.replace(url);
  };

  public startKaizenAd = async (): Promise<SingleDirectorUserFragment> => {
    let user: SingleDirectorUserFragment;

    try {
      user = await this.userRegistrationWebApi.signUpAsDirector();
    } catch (err) {
      if (err instanceof SignUpFailed) {
        this.authenticationFailed(err);
      }

      throw err;
    }

    return user;
  };

  public signOut = async (): Promise<void> => {
    await this.disposeSession();
    this.disposeUser();
    this.anonymizeExternalServices();
  };

  private disposeSession = async (): Promise<void> => {
    try {
      await this.authenticationWebApi.signOut();
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public signInFromPreviousSession = async (): Promise<void> => {
    this.store.dispatch(startAuthentication());

    try {
      await this.identify();
    } catch (error) {
      this.authenticationFailed(error);
    } finally {
      this.store.dispatch(finishAuthentication());
    }
  };

  // これいるの？ prepareUser で取ってるんじゃないの？
  public fetchDirectorUser = async (): Promise<SingleDirectorUserFragment | void> => {
    let user: SingleDirectorUserFragment;
    try {
      user = await this.userWebApi.getMe();
    } catch (error) {
      throw new Error('Fetching user has failed'); // message for Sentry
    }
    this.store.dispatch(setSignedInUser(user));
    window.newrelic.setCustomAttribute('user_id', user.id);
    window.newrelic.setCustomAttribute('user_email', user.email || '');
    return user;
  };

  public setInitOrganization = async (): Promise<void> => {
    if (location.href.match(/\?amazonAdAuthCode=/)) {
      const query = parse(location.search.substr(1));
      const orgId = query.org;
      try {
        if (orgId) {
          const organization = await this.organizationWebApi.getById(orgId);
          this.store.dispatch(setLastAccessedOrganization(organization));
        }
      } catch (err) {
        this.errorPresenter.showAsFatal(err);
        return;
      }
    } else {
      return;
    }
  };

  private identify = async (): Promise<void> => {
    let organizations: Array<OrganizationDetailFragment>;
    let user: SingleDirectorUserFragment | void;

    try {
      organizations = await this.prepareOrganizations();
      user = await this.prepareUser();
    } catch (error) {
      this.authenticationFailed(error);
      throw error;
    }

    if (!user) {
      return; // Show TOS agreement
    }

    this.identifyExternalServices(user, organizations);
  };

  private prepareOrganizations = async (): Promise<Array<OrganizationDetailFragment>> => {
    this.store.dispatch(startFetchingOrganizations());

    let organizations: Array<OrganizationDetailFragment>;

    try {
      organizations = await this.organizationWebApi.getAllJoinedOrganizations();
    } catch (error) {
      this.store.dispatch(finishFetchingOrganizations([]));
      throw error;
    }

    this.store.dispatch(finishFetchingOrganizations(organizations));

    return organizations;
  };

  private prepareUser = async (): Promise<SingleDirectorUserFragment | void> => {
    let user: SingleDirectorUserFragment;

    try {
      user = await this.userWebApi.getMe();
    } catch (error) {
      if (error instanceof MeNotFound) {
        // 利用規約はIDで表示される
        // Director-webではUser登録のみを責務とする
        user = await this.startKaizenAd();
      } else {
        throw new Error('Fetching user has failed'); // message for Sentry
      }
    }

    // GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        user_id: user.id,
        user_email: user.email,
        user_name: user.name,
      });
    }

    this.store.dispatch(setSignedInUser(user));
    window.newrelic.setCustomAttribute('user_id', user.id);
    window.newrelic.setCustomAttribute('user_email', user.email || '');
    return user;
  };

  private disposeUser = () => {
    this.store.dispatch(disposeUser());
    window.newrelic.setCustomAttribute('user_id', 'signed_out');
    window.newrelic.setCustomAttribute('user_email', 'signed_out');
  };

  private authenticationFailed = (err: Error, message = 'message.authentication_has_failed'): void => {
    if (err instanceof OrganizationNotFound) {
      message = 'message.no_available_organizations';
    }

    this.errorPresenter.showAsNonFatal(new Error(message));
  };

  private identifyExternalServices = (
    user: SingleDirectorUserFragment,
    organizations: Array<OrganizationDetailFragment>,
  ): void => {
    this.logRocketService.identify(user);
    this.intercomService.identify(user, organizations);
  };

  private anonymizeExternalServices = (): void => {
    this.logRocketService.anonymize();
    this.intercomService.anonymize();
  };
}

class OrganizationNotFound extends Error {
  public readonly name = 'OrganizationNotFound';
  public readonly message = 'No available organization is found.';

  constructor() {
    super();
  }
}

export default AuthenticationUsecase;
